/**
 * Login component for user authentication
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Loader2, GraduationCap } from 'lucide-react';
import { loginWithEmailAndPassword, resetPassword } from '../../../services/firebase/authService';

/**
 * Login component
 * 
 * @param {Object} props - Component props
 * @param {Function} props.switchToRegister - Function to switch to register view
 * @returns {JSX.Element} Login component
 */
const Login = ({ switchToRegister }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear errors when typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // Handle login submission
  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Validate form
    const newErrors = {};
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.password) newErrors.password = 'Password is required';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setLoading(true);
    try {
      const userCredential = await loginWithEmailAndPassword(
        formData.email,
        formData.password
      );
      
      // Store token in local storage
      const token = await userCredential.user.getIdToken();
      localStorage.setItem('authToken', token);
      
      // Navigate to home page
      navigate('/home');
    } catch (error) {
      console.error('Login error:', error);
      
      // Handle different error codes
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        setErrors({ login: 'Invalid email or password' });
      } else if (error.code === 'auth/invalid-email') {
        setErrors({ email: 'Invalid email address' });
      } else {
        setErrors({ login: 'Login failed. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle password reset
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      setErrors({ email: 'Please enter your email address' });
      return;
    }
    
    setLoading(true);
    try {
      await resetPassword(formData.email);
      setResetSent(true);
    } catch (error) {
      console.error('Password reset error:', error);
      
      if (error.code === 'auth/user-not-found') {
        setErrors({ email: 'No account found with this email' });
      } else if (error.code === 'auth/invalid-email') {
        setErrors({ email: 'Invalid email address' });
      } else {
        setErrors({ reset: 'Failed to send reset email. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-8 mt-6 sm:mt-0">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8 mt-8 sm:mt-0">
          <div className="flex items-center justify-center mb-4">
            <div className="relative">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center transform rotate-12 shadow-lg">
                <span className="text-white font-bold text-2xl transform -rotate-12">V</span>
              </div>
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-800">
            <span className="text-green-500">Varsity</span>Link
          </h1>
          <div className="flex items-center justify-center space-x-2 mt-2">
            <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded-full flex items-center">
              <GraduationCap className="w-3 h-3 mr-1" />
              The Ultimate Student Connection Hub
            </span>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-lg p-6 space-y-6">
          <h2 className="text-2xl font-semibold text-gray-800 text-center">
            {forgotPassword ? 'Reset Password' : 'Welcome Back'}
          </h2>
          
          {forgotPassword ? (
            // Reset Password Form
            <form onSubmit={handleResetPassword} className="space-y-4">
              {resetSent ? (
                <div className="bg-green-50 text-green-600 p-4 rounded-xl text-sm">
                  <p>Reset link sent! Check your email inbox and follow the instructions to reset your password.</p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="email"
                        name="email"
                        placeholder="Email address"
                        value={formData.email}
                        onChange={handleChange}
                        className={`pl-10 pr-4 py-3 w-full bg-gray-50 border ${
                          errors.email ? 'border-red-500' : 'border-gray-200'
                        } rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent`}
                      />
                    </div>
                    {errors.email && (
                      <p className="text-red-500 text-sm">{errors.email}</p>
                    )}
                  </div>
                  
                  {errors.reset && (
                    <div className="bg-red-50 text-red-500 p-3 rounded-xl text-sm">
                      {errors.reset}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full font-medium py-3 rounded-xl flex items-center justify-center space-x-2 transition-colors ${
                      loading
                        ? 'bg-gray-300 cursor-not-allowed'
                        : 'bg-green-500 hover:bg-green-600 text-white'
                    }`}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <span>Send Reset Link</span>
                    )}
                  </button>
                </>
              )}

              <div className="text-center text-sm">
                <button
                  type="button"
                  onClick={() => setForgotPassword(false)}
                  className="text-green-500 hover:text-green-600 font-medium"
                >
                  Back to Login
                </button>
              </div>
            </form>
          ) : (
            // Login Form
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    name="email"
                    placeholder="Email address"
                    value={formData.email}
                    onChange={handleChange}
                    className={`pl-10 pr-4 py-3 w-full bg-gray-50 border ${
                      errors.email ? 'border-red-500' : 'border-gray-200'
                    } rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent`}
                  />
                </div>
                {errors.email && (
                  <p className="text-red-500 text-sm">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="password"
                    name="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`pl-10 pr-4 py-3 w-full bg-gray-50 border ${
                      errors.password ? 'border-red-500' : 'border-gray-200'
                    } rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent`}
                  />
                </div>
                {errors.password && (
                  <p className="text-red-500 text-sm">{errors.password}</p>
                )}
              </div>

              <div className="flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => setForgotPassword(true)}
                  className="text-sm text-green-500 hover:text-green-600"
                >
                  Forgot password?
                </button>
              </div>

              {errors.login && (
                <div className="bg-red-50 text-red-500 p-3 rounded-xl text-sm">
                  {errors.login}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className={`w-full font-medium py-3 rounded-xl flex items-center justify-center space-x-2 transition-colors ${
                  loading
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-green-500 hover:bg-green-600 text-white'
                }`}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Logging in...</span>
                  </>
                ) : (
                  <span>Log in</span>
                )}
              </button>

              <div className="text-center text-sm">
                <span className="text-gray-600">Don't have an account?</span>{' '}
                <button
                  type="button"
                  onClick={switchToRegister}
                  className="text-green-500 hover:text-green-600 font-medium"
                >
                  Sign up
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

    </div>
  );
};

export default Login;